import React, { useRef, useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { useFormik } from 'formik';
import ReactTooltip from 'react-tooltip';
import moment from 'moment';

// Components
import CustomDatePicker from '../../components/CustomDatePicker';
import Label from '../../components/Label';
import CustomButton from '../../components/CustomButton';

// date-fns
import addMinutes from 'date-fns/addMinutes';

// Prime-React Components
import { classNames } from 'primereact/utils';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { Sidebar } from 'primereact/sidebar';

// Constants
import {
  INTERVIEW_TYPE,
  BUTTON_TYPES,
  LABEL_TYPES,
  STATUS,
  INTERVIEW_MODE,
  STATUS_INTERVIEW,
  InterviewMode,
  InterviewType,
  queryParamsOfFeedbackParams
} from '../../constants/common';
import { INTERVIEW_DURATION, NUMBER_OF_INTERVIEWERS } from '../../constants/dropdownOptions';

// Redux-Actions
import { allInterviewers } from '../../redux/actions/showOptions';
import {
  addInterviewRoundAction,
  editInterviewRoundAction,
  getInterviewDetails
} from '../../redux/actions/interviews';
import { getFilterCommonParamsList } from '../../redux/actions/commonParameter';
import { getHrAssignedJobsApi } from '../../redux/apis/hr';

// Utils
import { compareArrays } from '../../utils/array';
import {
  checkIsOnlyFeedbackUpdate,
  filterFeedbackParams,
  formatAvailabilityTime,
  wordCapitalize
} from '../../utils/common';

// Redux-slices
import { updateData } from '../../redux/slices/userDetails';
import { apiStatusClear } from '../../redux/slices/apiStatus';

// Components
import AvailabilityDetails from '../../components/popup/AvailabilityDetails';
import { AvailabilityButton } from '../../components/ActionButtons';
import { TextAreaWithCounterLimit } from '../../components/TextAreaWithCounterLimit';
import { Badge } from 'primereact/badge';
import { addEditInterviewRoundSchema } from '../../schema/interviewe.schema';
import FormikDropdown from '../../components/form/FormikDropdown';
import FormikMultiSelect from '../../components/form/FormikMultiSelect';
import FormikTextField from '../../components/form/FormikTextField';

const AddEditInterviewRound = (props) => {
  const { onHide, show, interviewRoundId, technologies: propTechnologies } = props;
  const defaultDate = moment().add(30, 'minutes').toDate();

  // React useRef hook.
  const selectedInterviewer = useRef([]);
  const meetingLinkRef = useRef(null);

  // React router dom hooks.
  const id = useParams()?.ID;
  const interviewRoundAddId = useParams()?.addingID;
  const { search } = useLocation();
  const ID = search.split('=')[1];

  // Redux hooks.
  const dispatch = useDispatch();
  const { InterviewDetails } = useSelector((state) => state.interviews);
  const { options, auth } = useSelector((state) => state);
  const { filterParameters } = useSelector((state) => state.parameter);
  const { isLoading, isSucceed, isFailed } = useSelector((state) => state.apiStatus);

  // React useState hooks.
  const [assignedJobs, setAssignedJobs] = useState([]);
  const [technologies, setTechnologies] = useState(propTechnologies || []);
  const [startDate, setStartDate] = useState(defaultDate);
  const [interviewerOptions, setInterviewerOptions] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [isUpdatedSuccess, setIsUpdatedSuccess] = useState(false);
  const [isFirstTime, setIsFirstTime] = useState(true);
  const [feedbackParamsOptions, setFeedbackParamsOptions] = useState([]);
  const [interviewRoundDetailsBackup, setInterviewRoundDetailsBackup] = useState(null);
  const [interviewData, setInterviewData] = useState({
    interviewerID: [],
    feedbackParameterIds: [],
    interviewType: '',
    jobId: '',
    interviewRoundMode: InterviewMode['Tatvasoft Office'],
    interviewStartDateTime: new Date(),
    interviewerLimit: NUMBER_OF_INTERVIEWERS[0],
    duration: 60,
    meetingLink: '',
    description: '',
    isRescheduleEnabled: false,
    isHrRound: true
  });

  const formik = useFormik({
    initialValues: interviewData,
    validationSchema: addEditInterviewRoundSchema,
    onSubmit: (values) => handleSubmit(values),
    enableReinitialize: true
  });

  const onTogglePopup = () => setShowPopup((prevState) => !prevState);

  const propTechKey = Array.isArray(propTechnologies)
    ? propTechnologies.join(',')
    : propTechnologies || '';
  const techKey = Array.isArray(technologies) ? technologies.join(',') : technologies || '';

  useEffect(() => {
    if (propTechnologies?.length && compareArrays(technologies, propTechnologies)) {
      setTechnologies(propTechnologies);
    }
  }, [propTechKey]);

  useEffect(() => {
    if (show && auth?.userId) {
      getHrAssignedJobsApi(auth.userId)
        .then((res) => {
          if (res?.data?.data) {
            setAssignedJobs((prev) => {
              const apiJobs = res.data.data;
              const missing = prev.filter((p) => !apiJobs.some((a) => a.ID === p.ID));
              return [...apiJobs, ...missing];
            });
          }
        })
        .catch((err) => console.log(err));
    }
  }, [show, auth?.userId]);

  useEffect(() => {
    if (formik.values.jobId && assignedJobs?.length > 0) {
      const selectedJob = assignedJobs.find((j) => j.ID === formik.values.jobId);
      const newTech = selectedJob?.technologies || [];
      if (newTech.length > 0 && compareArrays(technologies, newTech)) {
        setTechnologies(newTech);
      }
    }
  }, [formik.values.jobId, assignedJobs]);

  useEffect(() => {
    if (
      interviewRoundId &&
      InterviewDetails &&
      InterviewDetails?.RoundOfInterview &&
      Object.keys(InterviewDetails).length !== 0
    ) {
      const { RoundOfInterview } = InterviewDetails;

      const interviewRoundDetails = RoundOfInterview.filter((interviewRound) => {
        if (interviewRound.ID === interviewRoundId) {
          return true;
        }
      })[0];

      if (interviewRoundDetails) {
        const {
          InterviewRoundInvitations,
          InterviewRound,
          InterviewDuration,
          InterviewRoundMode,
          StartDateOfInterviewRound,
          InterviewerLimit,
          Description
        } = interviewRoundDetails;
        setInterviewRoundDetailsBackup(interviewRoundDetails);
        const interviewersIDs = InterviewRoundInvitations.filter((interviewerDetails) => {
          if (
            interviewerDetails?.Status === STATUS.ACCEPT ||
            interviewerDetails?.Status === STATUS.PENDING ||
            interviewerDetails?.Status === STATUS.RESCHEDULED
          )
            return true;
        }).map((interviewerDetail) => {
          return interviewerDetail?.Invited?.ID;
        });
        const selectedFeedbackParams = interviewRoundDetails?.IRMap?.map((param) => {
          return param?.ParentParameterID ? param?.ParentParameterID : param?.MapFp?.ID;
        });
        selectedInterviewer.current.value = interviewersIDs || [];

        const selectedJobId = interviewRoundDetails?.JobID || '';

        if (interviewRoundDetails?.JobDetails?.ID) {
          const roundJob = interviewRoundDetails.JobDetails;
          setAssignedJobs((prev) => {
            if (!prev.some((j) => j.ID === roundJob.ID)) {
              return [
                {
                  ID: roundJob.ID,
                  JobTitle: roundJob.JobTitle,
                  technologies: roundJob.Technology || []
                },
                ...prev
              ];
            }
            return prev;
          });
        }

        setInterviewData({
          ...interviewData,
          isHrRound: false,
          interviewerID: interviewersIDs || [],
          feedbackParameterIds: [...new Set(selectedFeedbackParams)],
          interviewType: InterviewRound,
          jobId: selectedJobId,
          duration: InterviewDuration,
          interviewRoundMode: InterviewRoundMode,
          interviewStartDateTime: new Date(StartDateOfInterviewRound),
          interviewerLimit: InterviewerLimit.toString(),
          description: Description,
          meetingLink: interviewRoundDetails?.meetingLink || ''
        });

        formik.setFieldValue('jobId', selectedJobId);

        // if time exist along with the interviewRoundId change the defaultDate value.
        const startDateSetter = moment(StartDateOfInterviewRound).isValid()
          ? moment(StartDateOfInterviewRound).toDate()
          : moment().toDate();
        setStartDate(startDateSetter);
      }
    }
  }, [InterviewDetails, interviewRoundId]);

  const interviewersOptions = (options) => {
    if (!options?.length) return [];
    return options?.map((opt) => {
      return {
        ...opt,
        name: `${wordCapitalize(opt.FirstName)} ${wordCapitalize(opt.LastName)}`,
        search: `${opt.FirstName} ${opt.LastName} [${opt?.UMap?.map((tech) =>
          wordCapitalize(tech?.MapT?.Technology)
        ).join(', ')}]`,
        code: `${opt.ID}`,
        key: `${opt.ID}`,
        disabled: !opt.IsAvailable,
        isAvailable: opt.IsAvailable
      };
    });
  };

  useEffect(() => {
    const modifiedInterviewerOptions = interviewersOptions(options);
    if (InterviewDetails?.RoundOfInterview?.length) {
      const interviewRound = InterviewDetails?.RoundOfInterview?.filter(
        (round) => round.ID === interviewRoundId
      );
      if (interviewRound?.length && interviewRound?.[0]?.InterviewRoundInvitations?.length) {
        interviewRound?.[0]?.InterviewRoundInvitations.filter((interviewerDetails) => {
          if (
            interviewerDetails?.Status === STATUS.ACCEPT ||
            interviewerDetails?.Status === STATUS.PENDING ||
            interviewerDetails?.Status === STATUS.RESCHEDULED
          )
            return true;
        }).forEach((interviewer) => {
          const interviewerData = interviewer?.Invited;
          let selectedInterviewer = formik.values.interviewerID;
          if (selectedInterviewer.indexOf(interviewerData.ID) === -1) {
            selectedInterviewer.push(interviewerData.ID);
            formik.setFieldValue('interviewerID', selectedInterviewer);
          }
          if (
            [
              STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_SCHEDULE,
              STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_SCHEDULE,
              STATUS_INTERVIEW.MANAGEMENT_SCHEDULE,
              STATUS_INTERVIEW.PRACTICAL_SCHEDULE,
              STATUS_INTERVIEW.HR_SCHEDULE
            ].indexOf(InterviewDetails.Status) !== -1 &&
            !modifiedInterviewerOptions.some((option) => option.code === interviewer.InterviewerID)
          ) {
            modifiedInterviewerOptions.push({
              name: `${interviewerData.FirstName} ${interviewerData.LastName}`,
              code: `${interviewerData.ID}`,
              ID: `${interviewerData.ID}`,
              key: `${interviewerData.ID}`,
              disabled: interviewerData?.UserStatus === 2
            });
          }
        });
      }
    }
    setInterviewerOptions(modifiedInterviewerOptions);
  }, [options]);

  const interviewerTemplate = (option) => {
    return (
      <>
        <ReactTooltip />
        <div data-tip={`${formatAvailabilityTime(option.InterviewerToAvailability)}`}>
          {!option.isAvailable ? (
            <span className="unavailable-interviewer-text-color mr-1">{option.name}</span>
          ) : (
            <span className="mr-1">{option.name}</span>
          )}
          {!!option?.UMap?.length && (
            <span className={`${!option.isAvailable && 'unavailable-interviewer-text-color'} mr-1`}>
              [{option?.UMap?.map((tech) => wordCapitalize(tech?.MapT?.Technology)).join(', ')}]
            </span>
          )}
          {!option.isAvailable && (
            <>
              {formatAvailabilityTime(option.InterviewerToAvailability) && (
                <span className="unavailable-interviewer-text-color mr-1">{`[${formatAvailabilityTime(
                  option.InterviewerToAvailability
                )}]`}</span>
              )}
              {/* <i
                className="pi pi-exclamation-triangle unavailable-interviewer-text-color"
                data-tip="Might not be available for the above schedule selected"
              /> */}
            </>
          )}
          {!!option?.IsPracticalAdmin && (
            <Badge value="Practical Admin" className="practical-admin" severity="success"></Badge>
          )}
        </div>
      </>
    );
  };

  const onDateChange = (value) => {
    setStartDate(value);
  };

  useEffect(() => {
    if (show && isUpdatedSuccess && isSucceed) {
      onHide(false);
      document.body.classList.remove('sidebar-open');
      formik.resetForm();
      dispatch(updateData());
      dispatch(apiStatusClear());
      setInterviewData({
        interviewerID: [],
        feedbackParameterIds: [],
        interviewType: '',
        interviewRoundMode: InterviewMode['Tatvasoft Office'],
        interviewStartDateTime: new Date(),
        interviewerLimit: NUMBER_OF_INTERVIEWERS[0],
        duration: 60,
        description: '',
        isRescheduleEnabled: false,
        isHrRound: true
      });
      setIsUpdatedSuccess(false);
    }
    if (isFailed) {
      setIsUpdatedSuccess(false);
    }
  }, [isFailed, isSucceed]);

  useEffect(() => () => setIsUpdatedSuccess(false), []);

  useEffect(() => {
    if (formik.values.interviewRoundMode === InterviewMode.Online) {
      setTimeout(() => {
        meetingLinkRef.current?.blur();
      }, 0);
    } else {
      formik.setFieldValue('meetingLink', '');
      formik.setFieldTouched('meetingLink', false);
    }
  }, [formik.values.interviewRoundMode]);

  const handleSubmit = (values) => {
    try {
      let selectedFeedbackParameterIds = filterFeedbackParams(
        values.feedbackParameterIds,
        feedbackParamsOptions
      );
      const isOnlineMode = values.interviewRoundMode === InterviewMode.Online;
      let formData = {
        interviewStartDateTime: startDate,
        interviewEndDateTime: addMinutes(startDate, values.duration),
        interviewerLimit: values.interviewerLimit,
        description: values.description,
        ...(values.interviewerID?.length && {
          interviewerID: values.interviewerID?.length ? values.interviewerID.join(',') : ''
        }),
        ...(isOnlineMode && values.meetingLink && { meetingLink: values.meetingLink.trim() }),
        ...(selectedFeedbackParameterIds && {
          feedbackParameters: selectedFeedbackParameterIds
        }),
        interviewRound: values.interviewType,
        ...(values.jobId && { jobId: values.jobId }),
        interviewDuration: values.duration,
        interviewRoundMode: values.interviewRoundMode
      };
      if (interviewRoundId) {
        formData = {
          interviewStartDateTime: startDate,
          interviewEndDateTime: addMinutes(startDate, values.duration),
          interviewDuration: values.duration,
          interviewerLimit: values.interviewerLimit,
          description: values.description?.trim(),
          interviewerID: values.interviewerID.join(','),
          ...(isOnlineMode && values.meetingLink && { meetingLink: values.meetingLink.trim() }),
          ...(selectedFeedbackParameterIds && {
            feedbackParameters: selectedFeedbackParameterIds
          }),
          interviewType: values.interviewType,
          ...(values.jobId && { jobId: values.jobId }),
          isOnlyFeedbackChange: checkIsOnlyFeedbackUpdate(
            interviewRoundDetailsBackup,
            values,
            startDate
          ),
          interviewRoundMode: values.interviewRoundMode
        };
      }

      let formdataJson = JSON.stringify(formData);

      if (interviewRoundId) {
        dispatch(
          editInterviewRoundAction({
            formdataJson,
            interviewRoundID: interviewRoundId
          })
        );
      } else {
        dispatch(addInterviewRoundAction({ interviewID: id, formdataJson }));
      }
    } catch (error) {
      console.log(error);
    }
    setIsUpdatedSuccess(true);
  };

  const isFormFieldValid = (name) => !!(formik.touched[name] && formik.errors[name]);

  useEffect(() => {
    if (interviewRoundId && selectedInterviewer.current.value) {
      const selectedInterviewRound = InterviewDetails?.RoundOfInterview?.filter(
        (interviewRound) => {
          if (interviewRound?.ID === interviewRoundId) return true;
        }
      )[0];
      const interviewRoundStartDate = new Date(
        selectedInterviewRound?.StartDateOfInterviewRound?.toString()
      );
      const interviewRoundDuration = selectedInterviewRound?.InterviewDuration;

      const interviewRoundStatus = InterviewDetails?.RoundOfInterview?.filter((interviewRound) => {
        if (interviewRound?.ID === interviewRoundId) return true;
      })[0]?.Status;

      if (
        (interviewRoundStatus === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_SCHEDULE ||
          interviewRoundStatus === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_SCHEDULE ||
          interviewRoundStatus === STATUS_INTERVIEW.PRACTICAL_SCHEDULE ||
          interviewRoundStatus === STATUS_INTERVIEW.HR_SCHEDULE ||
          interviewRoundStatus === STATUS_INTERVIEW.MANAGEMENT_SCHEDULE ||
          interviewRoundStatus === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_1_RESCHEDULE ||
          interviewRoundStatus === STATUS_INTERVIEW.PRACTICAL_RESCHEDULE ||
          interviewRoundStatus === STATUS_INTERVIEW.TECHNICAL_INTERVIEW_2_RESCHEDULE ||
          interviewRoundStatus === STATUS_INTERVIEW.MANAGEMENT_RESCHEDULE ||
          interviewRoundStatus === STATUS_INTERVIEW.HR_RESCHEDULE) &&
        ((startDate && !moment(startDate).isSame(moment(interviewRoundStartDate))) ||
          formik.values.duration !== interviewRoundDuration ||
          compareArrays(
            formik.values.interviewerID,
            interviewRoundDetailsBackup?.InterviewRoundInvitations?.filter(
              (inv) =>
                inv.Status === STATUS.ACCEPT ||
                inv.Status === STATUS.PENDING ||
                inv.Status === STATUS.RESCHEDULED
            )?.map((inv) => inv?.Invited?.ID) || []
          ) ||
          formik.values.interviewRoundMode !== selectedInterviewRound?.InterviewRoundMode)
      ) {
        formik.setFieldValue('isRescheduleEnabled', true);
      } else {
        formik.setFieldValue('isRescheduleEnabled', false);
      }
    }
  }, [
    interviewRoundId,
    startDate,
    formik.values.duration,
    interviewData.interviewStartDateTime,
    selectedInterviewer.current.value,
    interviewData.interviewerID,
    formik.values.interviewRoundMode
  ]);

  useEffect(() => {
    if (!show) {
      setStartDate(defaultDate);
    }
  }, [show]);

  // React useEffect hooks.
  useEffect(() => {
    interviewRoundId && !startDate && dispatch(getInterviewDetails(id));
    interviewRoundAddId && dispatch(getInterviewDetails(interviewRoundAddId));
    if (startDate) {
      const endDate = addMinutes(startDate, formik.values.duration);
      const interviewStartTime = moment(startDate).format();
      const interviewEndTime = moment(endDate).format();
      startDate &&
        dispatch(
          allInterviewers(
            interviewStartTime,
            interviewEndTime,
            technologies?.length ? technologies : '',
            interviewRoundId || '',
            formik.values.interviewType === InterviewType['HR Round'],
            formik.values.interviewType === InterviewType.Practical
          )
        );
    }
  }, [
    dispatch,
    interviewRoundId,
    startDate,
    formik.values.interviewType,
    formik.values.duration,
    interviewRoundAddId,
    techKey
  ]);

  // React useEffect hooks.
  useEffect(() => {
    if (!interviewRoundId && startDate) {
      formik.setFieldValue('interviewerID', []);
    }
  }, [formik.values.interviewType, startDate]);

  const feedbackParamsOptionsGenerate = (options) => {
    if (!options?.length) return [];

    const committedSingleParams = {
      single: new Set(),
      group: new Set()
    };
    const selectedParameterIds = new Set(formik.values.feedbackParameterIds);

    // Collect committed single parameters
    options.forEach((opt) => {
      if (selectedParameterIds.has(opt.ID)) {
        if (opt.GroupParameters?.length) {
          opt.GroupParameters.forEach((child) =>
            committedSingleParams.group.add(child.Parameter.ID)
          );
        } else {
          committedSingleParams.single.add(opt.ID);
        }
      }
    });

    // Generate parameter options
    const paramsOptions = options.map((opt) => ({
      name: opt.ParameterName,
      code: opt.ID,
      key: opt.ID,
      childParams:
        opt.GroupParameters?.map((child) => ({
          id: child.Parameter.ID || '',
          parameterName: child.Parameter.ParameterName || ''
        })) || [],
      disabled: isOptionDisabled(opt, selectedParameterIds, committedSingleParams)
    }));

    return paramsOptions;
  };

  // Helper function to determine if an option is disabled
  const isOptionDisabled = (opt, selectedParameterIds, committedSingleParams) => {
    if (selectedParameterIds.has(opt.ID)) {
      if (opt.GroupParameters?.length) {
        opt.GroupParameters.forEach((child) => {
          if (selectedParameterIds.has(child.Parameter.ID)) {
            // Remove the child ID from selectedParameterIds
            selectedParameterIds.delete(child.Parameter.ID);
            formik.setFieldValue('feedbackParameterIds', [...selectedParameterIds]);
          }
        });
      }
      return false;
    }

    if (opt.GroupParameters?.length) {
      return opt.GroupParameters.some((child) =>
        committedSingleParams.group.has(child.Parameter.ID)
      );
    }

    return committedSingleParams.single.has(opt.ID) || committedSingleParams.group.has(opt.ID);
  };

  useEffect(() => {
    const modifiedOptions = feedbackParamsOptionsGenerate(filterParameters || []);
    setFeedbackParamsOptions(modifiedOptions);
  }, [filterParameters, formik.values.feedbackParameterIds]);

  useEffect(() => {
    if (show) {
      dispatch(getFilterCommonParamsList(queryParamsOfFeedbackParams));
    }
  }, [show]);

  const feedBackParamsTemplate = (option) => {
    return (
      <>
        <span className="mr-1">{wordCapitalize(option.name)}</span>
        {!!option?.childParams?.length && (
          <span>
            [{option?.childParams?.map((tech) => wordCapitalize(tech?.parameterName)).join(', ')}]
          </span>
        )}
      </>
    );
  };

  return (
    <>
      <AvailabilityDetails onHide={onTogglePopup} show={showPopup} />
      <ConfirmDialog />
      <Sidebar
        ID={ID}
        visible={show}
        onHide={() => {
          onHide(true);
          document.body.classList.remove('sidebar-open');
        }}
        position="right"
        className="sidebar-drawer">
        <div className="form-box-wrapper">
          <div className="title-wrapper">
            <p className="card-title">{interviewRoundId ? 'Edit' : 'Schedule'} Interview Round</p>
            <button
              className="p-sidebar-close"
              onClick={() => {
                onHide(false);
                document.body.classList.remove('sidebar-open');
                setInterviewData({
                  interviewerID: [],
                  feedbackParameterIds: [],
                  interviewType: '',
                  interviewRoundMode: InterviewMode['Tatvasoft Office'],
                  interviewStartDateTime: new Date(),
                  interviewerLimit: NUMBER_OF_INTERVIEWERS[0],
                  duration: 60,
                  description: '',
                  isRescheduleEnabled: false,
                  isHrRound: true
                });
              }}>
              <span className="p-sidebar-close-icon pi pi-times" />
            </button>
          </div>
          <form
            onSubmit={formik.handleSubmit}
            className="p-fluid"
            autoComplete="off"
            encType="multipart/form-data">
            <div className="form-row-wrapper">
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <FormikDropdown
                      labelText="Job Role"
                      isMandatory
                      formik={formik}
                      name="jobId"
                      optionLabel="JobTitle"
                      optionValue="ID"
                      options={assignedJobs}
                      placeholder="Select Job Role *"
                      onChange={(e) => {
                        formik.handleChange(e);
                        const selectedJob = assignedJobs?.find((j) => j.ID === e.value);
                        if (selectedJob?.technologies?.length) {
                          setTechnologies(selectedJob.technologies);
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className="form-col full-width">
                <div className="custom-form-group">
                  <FormikDropdown
                    labelText={LABEL_TYPES.INTERVIEW_TYPE}
                    isMandatory
                    formik={formik}
                    name="interviewType"
                    optionLabel="name"
                    optionValue="code"
                    options={!ID ? INTERVIEW_TYPE : INTERVIEW_TYPE.filter((type) => type.code <= 5)}
                    disabled={!!interviewRoundId}
                    placeholder="Select Interview Type *"
                    onChange={(e) => {
                      formik.handleChange(e);
                      if (isFirstTime && technologies?.length) {
                        formik.setFieldValue('feedbackParameterIds', technologies);
                      } else {
                        formik.setFieldValue('feedbackParameterIds', []);
                      }
                    }}
                  />
                </div>
              </div>
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <FormikDropdown
                      labelText={LABEL_TYPES.INTERVIEW_ROUND_MODE}
                      isMandatory
                      formik={formik}
                      name="interviewRoundMode"
                      optionLabel="name"
                      optionValue="code"
                      options={INTERVIEW_MODE}
                      placeholder="Select Interview Mode"
                    />
                  </div>
                </div>
              </div>
              <div className="form-col full-width">
                <div className="custom-form-group">
                  <CustomDatePicker
                    value={startDate}
                    onChange={onDateChange}
                    minDateTime={new Date().setDate(new Date().getDate())}
                    minTime={new Date().setHours(0, 0, 0)}
                    maxTime={new Date().setHours(23, 59, 59)}
                  />
                </div>
              </div>
              <div className="form-col full-width">
                <div className="form-group-outer">
                  <div className="custom-form-group">
                    <FormikDropdown
                      labelText={LABEL_TYPES.INTERVIEW_DURATION}
                      isMandatory
                      formik={formik}
                      name="duration"
                      optionLabel="name"
                      optionValue="code"
                      options={INTERVIEW_DURATION}
                      placeholder="Select Interview Duration"
                    />
                  </div>
                </div>
              </div>
              <div className="form-col full-width">
                <div className="custom-form-group">
                  <Label
                    htmlFor="interviewerLimit"
                    text={LABEL_TYPES.INTERVIEWER_LIMIT}
                    isChildren
                    isMandatory
                    tooltip="Sets the maximum no. of interviewers can join the interview."
                  />
                  <span className="p-input-icon-right">
                    <FormikDropdown
                      isMandatory
                      formik={formik}
                      name="interviewerLimit"
                      options={NUMBER_OF_INTERVIEWERS}
                      placeholder="Select Number of Interviewers"
                    />
                  </span>
                </div>
              </div>
              <div className="form-col full-width">
                <div className="custom-form-group">
                  <Label
                    htmlFor="interviewerIDs"
                    text={LABEL_TYPES.INTERVIEWERS}
                    isMandatory={formik.values.interviewType}
                  />
                  <AvailabilityButton onClick={onTogglePopup} />
                  <span className="p-input-icon-right">
                    <FormikMultiSelect
                      key={interviewerOptions?.length}
                      isMandatory
                      formik={formik}
                      name="interviewerID"
                      placeholder="Select Interviewer"
                      optionLabel="name"
                      optionValue="code"
                      options={interviewerOptions}
                      onChange={(e) => {
                        formik.handleChange(e);
                        selectedInterviewer.current.value = e.target.value;
                      }}
                      itemTemplate={interviewerTemplate}
                      filterBy="search"
                    />
                  </span>
                </div>
              </div>
              {(formik.values.interviewType === InterviewType['Technical Interview - 1'] ||
                formik.values.interviewType === InterviewType['Technical Interview - 2'] ||
                formik.values.interviewType === InterviewType['Management Round']) && (
                <div className="form-col full-width">
                  <div className="form-group-outer">
                    <div className="custom-form-group">
                      <FormikMultiSelect
                        key={feedbackParamsOptions?.length}
                        labelText={LABEL_TYPES.FEEDBACK_PARAMETERS}
                        isMandatory
                        formik={formik}
                        name="feedbackParameterIds"
                        placeholder="Select Feedback Params"
                        optionLabel="name"
                        optionValue="code"
                        options={feedbackParamsOptions}
                        onChange={(e) => {
                          formik.handleChange(e);
                          setIsFirstTime(false);
                        }}
                        itemTemplate={feedBackParamsTemplate}
                        showSelectAll={false}
                      />
                    </div>
                  </div>
                </div>
              )}
              {formik.values.interviewRoundMode === InterviewMode.Online && (
                <div className="form-col full-width">
                  <div className="form-group-outer">
                    <div className="custom-form-group">
                      <FormikTextField
                        labelText="Interview Meeting Link"
                        isMandatory
                        formik={formik}
                        name="meetingLink"
                        type="url"
                        placeholder="https://meet.google.com/..."
                        className="w-full"
                        ref={meetingLinkRef}
                      />
                    </div>
                  </div>
                </div>
              )}
              <div className="form-col full-width">
                <div className="custom-form-group">
                  <span className="p-input-icon-right">
                    <TextAreaWithCounterLimit
                      placeholder={LABEL_TYPES.DESCRIPTION}
                      labelText={LABEL_TYPES.DESCRIPTION}
                      value={formik.values.description}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      errorMessage={
                        formik.touched['description'] ? formik.errors['description'] : ''
                      }
                      className={classNames({
                        'p-invalid': isFormFieldValid('description'),
                        'w-full': true
                      })}
                      rowsValue={1}
                      id="description"
                      name="description"
                      htmlFor="description"
                      validationOnMinLimit
                    />
                  </span>
                </div>
              </div>
            </div>
            <div className="form-btn-wrapper">
              <CustomButton
                type="submit"
                variant="contained"
                className="mb-3 w-auto"
                disabled={isLoading || formik.values.isRescheduleEnabled}>
                {BUTTON_TYPES.SAVE}
              </CustomButton>
              {interviewRoundId && (
                <CustomButton
                  type="submit"
                  variant="outlined"
                  className="mb-3 w-auto"
                  disabled={isLoading || !formik.values.isRescheduleEnabled}>
                  {BUTTON_TYPES.SAVE_RESCHEDULE}
                </CustomButton>
              )}
              <CustomButton
                variant="contained"
                onClick={() => {
                  onHide(false);
                  document.body.classList.remove('sidebar-open');
                  setInterviewData({
                    interviewerID: [],
                    interviewType: '',
                    interviewStartDateTime: new Date(),
                    interviewerLimit: NUMBER_OF_INTERVIEWERS[0],
                    duration: 60,
                    description: '',
                    isRescheduleEnabled: false,
                    isHrRound: true
                  });
                }}
                color="error"
                className="mb-3 w-auto gray-btn">
                {BUTTON_TYPES.CANCEL}
              </CustomButton>
            </div>
          </form>
        </div>
      </Sidebar>
    </>
  );
};

export default AddEditInterviewRound;
